import * as React from 'react';
import { Search, X } from 'lucide-react';
import { Input } from '../ui/input';
import { cn } from '../../lib/utils';

interface SearchInputProps extends Omit<
  React.ComponentProps<'input'>,
  'value' | 'onChange' | 'type' | 'size'
> {
  value: string;
  onValueChange: (value: string) => void;
  /** Visual size of the input. `sm` is for dense toolbars/sidebars. */
  size?: 'sm' | 'md';
  /** Hide the clear (×) button even when the value is non-empty. */
  hideClear?: boolean;
  containerClassName?: string;
}

const SIZE_CLASSES = {
  sm: {
    input: 'h-7 pl-7 pr-7 text-xs md:text-xs',
    icon: 'left-2 size-3.5',
    clear: 'right-1.5 size-5',
    clearIcon: 'size-3',
  },
  md: {
    input: 'h-9 pl-9 pr-9',
    icon: 'left-3 size-4',
    clear: 'right-2 size-6',
    clearIcon: 'size-3.5',
  },
} as const;

/**
 * Standard search input with a leading search icon and a trailing clear button.
 * Use for client-side filtering of any list. Controlled — pass `value` and
 * `onValueChange`. Built on the shared `Input` primitive so focus/hover styles
 * stay consistent with the rest of the design system.
 */
export function SearchInput({
  value,
  onValueChange,
  placeholder = 'Search…',
  size = 'md',
  hideClear,
  className,
  containerClassName,
  'aria-label': ariaLabel = 'Search',
  ...props
}: SearchInputProps) {
  const sizes = SIZE_CLASSES[size];
  const showClear = !hideClear && value.length > 0;

  return (
    <div className={cn('relative flex-1', containerClassName)}>
      <Search
        aria-hidden
        className={cn(
          'pointer-events-none absolute top-1/2 -translate-y-1/2 text-muted-foreground',
          sizes.icon,
        )}
      />
      <Input
        type="search"
        role="searchbox"
        value={value}
        onChange={(e) => onValueChange(e.target.value)}
        placeholder={placeholder}
        aria-label={ariaLabel}
        className={cn(
          '[&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none',
          sizes.input,
          className,
        )}
        {...props}
      />
      {showClear && (
        <button
          type="button"
          aria-label="Clear search"
          onClick={() => onValueChange('')}
          className={cn(
            'absolute top-1/2 -translate-y-1/2 flex items-center justify-center rounded-sm text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            sizes.clear,
          )}
        >
          <X className={sizes.clearIcon} />
        </button>
      )}
    </div>
  );
}
